import { useCallback, useEffect, useMemo, useState } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  SxProps,
  TextField,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import type { CustomUser } from 'api/models/CustomUser';
import { WorkboardTask } from 'api/models/WorkboardTask';
import { ApiService } from 'api/services/ApiService';
import { DashboardService } from 'api/services/DashboardService';
import { useZoom } from 'context/ZoomContext';
import { CALENDAR_DATE_FORMAT } from 'dashboard409a/common/constants/calendar';
import { WORKBOARD_TASK_CALENDAR_EVENTS_QUERY_KEY } from 'dashboard409a/hooks/useWorkboardTaskCalendarEvents';
import { ConditionalLoader, Section, ValuationStatusTracker } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import {
  AvatarRow,
  DateRow,
  DeleteModalActions,
  DeleteModalCloseButtonContainer,
  DeleteModalContainer,
  FooterBox,
  RoleBox,
  StatusRow,
  StyledBox,
  Wrapper,
} from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.styles';
import AnalystNotesHistoryPanel from 'dashboard409a/pages/Operations/components/NotesDisplay/AnalystNotesHistoryPanel';
import { Container, InfoRow, StyledLinkSpan } from 'dashboard409a/pages/Operations/components/ValuedEntity/ValuedEntity.styles';
import {
  GroupedUserAutocomplete,
  type GroupedUserAutocompleteDisplayItem,
} from 'dashboard409a/pages/Workboard/Filters/GroupedUserAutocomplete';
import type { RecurrenceEndMode } from './CreateWorkboardTaskModal.constants';
import {
  groupedUserAutocompleteInputSx,
  RECURRENCE_FREQUENCY_OPTIONS,
  RECURRENCE_SETPOS_OPTIONS,
  RECURRENCE_WEEKDAY_OPTIONS,
  TASK_STATUS_TRACKER_OPTIONS,
} from './CreateWorkboardTaskModal.constants';
import { CreateWorkboardTaskModalProps } from './CreateWorkboardTaskModal.props';
import {
  CloseButtonContainer,
  FieldLabel,
  FolderLocation,
  ModalFooter,
  StatusHeaderRow,
  TaskNameField,
  WorkboardTaskBentoGrid,
  workboardTaskModalBoxSx,
} from './CreateWorkboardTaskModal.styles';
import {
  buildWorkboardTaskPayload,
  type CreateWorkboardTaskFormState,
  getCustomUserAvatarData,
  getCustomUserDisplayName,
  getCustomUsersAvatarData,
  getCustomUsersDisplayName,
  getDefaultFormState,
  getWeekdayCodeFromDate,
  mapWorkboardTaskToFormState,
  validateCreateWorkboardTaskForm,
} from './CreateWorkboardTaskModal.utilities';

type UserRoleField = 'assignedTo' | 'assignedTaskManager';

const CreateWorkboardTaskModal = ({
  open,
  onClose,
  defaultStartsOn,
  taskId = null,
  occurrenceDate = null,
}: CreateWorkboardTaskModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { zoomLevel } = useZoom();
  const isEditMode = taskId != null;
  const [form, setForm] = useState<CreateWorkboardTaskFormState>(() => getDefaultFormState(defaultStartsOn));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [assigneeInput, setAssigneeInput] = useState('');
  const [taskManagerInput, setTaskManagerInput] = useState('');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { data: staffUsers = [], isLoading: isLoadingStaff } = useQuery<CustomUser[]>({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return (response.filter(user => !user.email?.includes('+')) as CustomUser[]) || [];
    },
    staleTime: 60 * 60 * 1000,
    enabled: open,
  });

  const { data: existingTask, isLoading: isLoadingTask } = useQuery({
    queryKey: ['workboard-task', taskId],
    queryFn: () => DashboardService.apiDashboardWorkboardTasksRead(String(taskId)),
    enabled: open && isEditMode,
  });

  useEffect(() => {
    if (!open) {
      setAssigneeInput('');
      setTaskManagerInput('');
      setIsDeleteConfirmOpen(false);
      return;
    }

    setSubmitError(null);

    if (isEditMode) {
      if (existingTask) {
        setForm(mapWorkboardTaskToFormState(existingTask, staffUsers, occurrenceDate));
      }
      return;
    }

    setForm(getDefaultFormState(defaultStartsOn));
  }, [open, isEditMode, defaultStartsOn, existingTask, staffUsers, taskId, occurrenceDate]);

  const isRecurring = form.recurrenceFrequency !== WorkboardTask.recurrence_frequency.NONE;
  const showWeekdayPicker =
    form.recurrenceFrequency === WorkboardTask.recurrence_frequency.WEEKLY ||
    (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY && form.recurrenceBySetpos !== null);
  const showSetposPicker = form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY;
  const fieldsDisabled = form.isWaiting || isSaving || isDeleting;
  const isFormLoading = isEditMode && isLoadingTask;
  const hasFolder = Boolean(form.fileReferenceFolder.trim());
  const notesOccurrenceDate = useMemo(() => {
    if (form.occurrenceDate) {
      return moment(form.occurrenceDate).format(CALENDAR_DATE_FORMAT);
    }

    return occurrenceDate;
  }, [form.occurrenceDate, occurrenceDate]);
  const workboardTaskOccurrence = useMemo(
    () =>
      isEditMode && taskId != null && notesOccurrenceDate
        ? { workboardTaskId: taskId, occurrenceDate: notesOccurrenceDate }
        : undefined,
    [isEditMode, taskId, notesOccurrenceDate]
  );
  const areNotesDisabled = !workboardTaskOccurrence || isSaving || isDeleting;

  const updateForm = <K extends keyof CreateWorkboardTaskFormState>(key: K, value: CreateWorkboardTaskFormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setSubmitError(null);
  };

  const handleRecurrenceFrequencyChange = (frequency: WorkboardTask.recurrence_frequency) => {
    setForm(prev => {
      const next: CreateWorkboardTaskFormState = {
        ...prev,
        recurrenceFrequency: frequency,
        recurrenceEndMode: frequency === WorkboardTask.recurrence_frequency.NONE ? 'none' : prev.recurrenceEndMode,
      };

      if (
        frequency === WorkboardTask.recurrence_frequency.WEEKLY &&
        next.recurrenceByWeekday.length === 0 &&
        next.occurrenceDate
      ) {
        next.recurrenceByWeekday = [getWeekdayCodeFromDate(next.occurrenceDate)];
      }

      return next;
    });
    setSubmitError(null);
  };

  const handleOccurrenceDateChange = (date: Date | null) => {
    setForm(prev => {
      const next: CreateWorkboardTaskFormState = {
        ...prev,
        occurrenceDate: date,
        startsOn:
          !isEditMode || prev.recurrenceFrequency === WorkboardTask.recurrence_frequency.NONE
            ? date
            : prev.startsOn,
      };

      if (
        date &&
        prev.recurrenceFrequency === WorkboardTask.recurrence_frequency.WEEKLY &&
        prev.recurrenceByWeekday.length === 0
      ) {
        next.recurrenceByWeekday = [getWeekdayCodeFromDate(date)];
      }

      return next;
    });
    setSubmitError(null);
  };

  const handleClose = () => {
    if (isSaving || isDeleting) {
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    const validationError = validateCreateWorkboardTaskForm(form);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setIsSaving(true);
    setSubmitError(null);
    try {
      if (isEditMode && taskId != null) {
        await DashboardService.apiDashboardWorkboardTasksUpdate(
          String(taskId),
          buildWorkboardTaskPayload(form, { forUpdate: true })
        );
        await queryClient.invalidateQueries({ queryKey: ['workboard-task', taskId] });
        enqueueSnackbar('Task updated', { variant: 'success' });
      } else {
        await DashboardService.apiDashboardWorkboardTasksCreate(buildWorkboardTaskPayload(form));
        enqueueSnackbar('Task created', { variant: 'success' });
      }
      await queryClient.invalidateQueries({ queryKey: [WORKBOARD_TASK_CALENDAR_EVENTS_QUERY_KEY] });
      onClose();
    } catch {
      enqueueSnackbar(
        isEditMode ? 'Failed to update task. Please try again.' : 'Failed to create task. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!isEditMode || taskId == null) {
      return;
    }

    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!isEditMode || taskId == null) {
      return;
    }

    setIsDeleting(true);
    try {
      await DashboardService.apiDashboardWorkboardTasksDelete(String(taskId));
      await queryClient.invalidateQueries({ queryKey: [WORKBOARD_TASK_CALENDAR_EVENTS_QUERY_KEY] });
      enqueueSnackbar('Task deleted', { variant: 'success' });
      setIsDeleteConfirmOpen(false);
      onClose();
    } catch {
      enqueueSnackbar('Failed to delete task. Please try again.', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const renderDialogTags = useCallback(
    (displayItems: GroupedUserAutocompleteDisplayItem[], maxVisible: number) => {
      if (displayItems.length === 0) {
        return null;
      }

      const visible = displayItems.slice(0, maxVisible);
      const remaining = displayItems.length - maxVisible;
      const chipSx: SxProps<Theme> = {
        height: 20,
        fontSize: '0.7rem',
        bgcolor: 'action.hover',
        '& .MuiChip-label': { px: 0.5 },
        '& .MuiChip-icon': { fontSize: '0.75rem', ml: '4px' },
      };

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden', mr: 0.5 }}>
          {visible.map((item, index) => (
            <Chip
              key={index}
              size="small"
              icon={<AccountCircleIcon />}
              label={
                item.kind === 'group'
                  ? item.data.name
                  : [item.data.first_name, item.data.last_name].filter(Boolean).join(' ') || item.data.email || ''
              }
              sx={chipSx}
            />
          ))}
          {remaining > 0 && (
            <Chip size="small" icon={<AccountCircleIcon />} label={`+${remaining}`} sx={chipSx} />
          )}
        </Box>
      );
    },
    []
  );

  const renderUserRoleBox = (
    field: UserRoleField,
    label: string,
    placeholder: string,
    inputValue: string,
    onInputChange: (value: string) => void,
    testId: string
  ) => {
    const users = form[field];

    return (
      <RoleBox>
        <Wrapper>
          <Typography fontWeight={600} fontSize="0.8rem">
            {label}
          </Typography>
        </Wrapper>
        {fieldsDisabled ? (
          <AvatarRow>
            {users.length > 0 ? (
              users.map(user => (
                <Avatar
                  key={user.id}
                  data={getCustomUserAvatarData(user)}
                  name={getCustomUserDisplayName(user)}
                  isLoading={isLoadingStaff}
                />
              ))
            ) : (
              <Avatar
                data={getCustomUsersAvatarData(users)}
                name={getCustomUsersDisplayName(users)}
                isLoading={isLoadingStaff}
              />
            )}
          </AvatarRow>
        ) : (
          <Box sx={{ mt: 0.5, width: '100%', minWidth: 160 }} data-testid={testId}>
            <GroupedUserAutocomplete
              value={users}
              onChange={selectedUsers => updateForm(field, selectedUsers)}
              inputValue={inputValue}
              onInputChange={onInputChange}
              label=""
              placeholder={placeholder}
              zoomLevel={zoomLevel}
              maxVisibleTags={2}
              renderTagsOverride={renderDialogTags}
              textFieldSx={groupedUserAutocompleteInputSx}
              disabled={isSaving || isDeleting}
              autocompleteRootSx={{ width: '100%' }}
            />
          </Box>
        )}
      </RoleBox>
    );
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={workboardTaskModalBoxSx} onClick={e => e.stopPropagation()}>
          <CloseButtonContainer>
            <IconButton onClick={handleClose} size="small" aria-label="close" sx={{ padding: '4px' }}>
              <CloseIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </CloseButtonContainer>

          {isFormLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <WorkboardTaskBentoGrid showNotesColumn={Boolean(workboardTaskOccurrence)}>
                <Section title="Task Details">
                  <Container>
                    <TaskNameField>
                      <SettingsIcon color="primary" fontSize="small" />
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Task name"
                        value={form.name}
                        onChange={e => updateForm('name', e.target.value)}
                        autoFocus
                        variant="standard"
                        InputProps={{
                          sx: theme => ({
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                          }),
                        }}
                        data-testid="create-workboard-task-name"
                      />
                    </TaskNameField>

                    <InfoRow>
                      <Box>
                        <FieldLabel>Task Manager</FieldLabel>
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {form.assignedTaskManager.length > 0 ? (
                            form.assignedTaskManager.map(user => (
                              <Avatar
                                key={user.id}
                                data={getCustomUserAvatarData(user)}
                                name={getCustomUserDisplayName(user)}
                                isLoading={isLoadingStaff}
                              />
                            ))
                          ) : (
                            <Avatar
                              data={getCustomUsersAvatarData(form.assignedTaskManager)}
                              name={getCustomUsersDisplayName(form.assignedTaskManager)}
                              isLoading={isLoadingStaff}
                            />
                          )}
                        </Box>
                      </Box>

                      <Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                          <FieldLabel>Documentation Folder</FieldLabel>
                          {hasFolder && <Chip label="Folder set" color="success" size="small" variant="outlined" />}
                        </Box>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Folder path"
                          value={form.fileReferenceFolder}
                          onChange={e => updateForm('fileReferenceFolder', e.target.value)}
                          sx={{ mt: 0.5 }}
                          data-testid="create-workboard-task-folder"
                        />
                        {hasFolder && (
                          <FolderLocation>
                          Location: {form.fileReferenceFolder.trim()}
                          </FolderLocation>
                        )}
                      </Box>

                      <Box>
                        <FieldLabel>Related Documents</FieldLabel>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Link to related documents"
                          value={form.linkToRelatedDocuments}
                          onChange={e => updateForm('linkToRelatedDocuments', e.target.value)}
                          sx={{ mt: 0.5 }}
                          data-testid="create-workboard-task-link"
                        />
                        {form.linkToRelatedDocuments.trim() && (
                          <Box sx={{ mt: 0.5 }}>
                            <Link href={form.linkToRelatedDocuments.trim()} target="_blank" rel="noopener noreferrer">
                              <StyledLinkSpan>Open link</StyledLinkSpan>
                            </Link>
                          </Box>
                        )}
                      </Box>

                      <Box>
                        <FieldLabel>Task Owner</FieldLabel>
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {form.assignedTo.length > 0 ? (
                            form.assignedTo.map(user => (
                              <Avatar
                                key={user.id}
                                data={getCustomUserAvatarData(user)}
                                name={getCustomUserDisplayName(user)}
                                isLoading={isLoadingStaff}
                              />
                            ))
                          ) : (
                            <Avatar
                              data={getCustomUsersAvatarData(form.assignedTo)}
                              name={getCustomUsersDisplayName(form.assignedTo)}
                              isLoading={isLoadingStaff}
                            />
                          )}
                        </Box>
                      </Box>
                    </InfoRow>
                  </Container>
                </Section>

                <StyledBox>
                  <Typography variant="h3" fontWeight={600} color="text.primary">
                  Task Status
                  </Typography>
                  <StatusHeaderRow>
                    <Tooltip
                      title="When enabled, status and due date fields are disabled."
                      arrow
                      placement="top"
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={form.isWaiting}
                            onChange={e => updateForm('isWaiting', e.target.checked)}
                            disabled={isSaving || isDeleting}
                            size="small"
                            data-testid="create-workboard-task-is-waiting"
                            sx={{
                              padding: '0px 8px',
                              color: 'primary.main',
                              '&.Mui-checked': { color: 'primary.main' },
                            }}
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {form.isWaiting && <PauseCircleIcon fontSize="small" color="primary" />}
                            <Typography variant="body2" color={form.isWaiting ? 'primary.main' : 'text.secondary'}>
                            Is Waiting
                            </Typography>
                          </Box>
                        }
                        sx={{ mr: 0 }}
                      />
                    </Tooltip>
                    {isEditMode && (
                      <Tooltip title="Delete task">
                        <IconButton size="small" onClick={handleDeleteClick} disabled={isSaving || isDeleting}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </StatusHeaderRow>

                  <Box flex={1} display="flex" flexDirection="column" width="100%">
                    <DateRow sx={{ mt: 1 }}>
                      <Typography fontWeight={600} fontSize="0.85rem">
                      Task Due Date
                      </Typography>
                      <ConditionalLoader isLoading={false} width={150}>
                        <MobileDatePicker
                          value={form.occurrenceDate}
                          onChange={handleOccurrenceDateChange}
                          format="MM/dd/yyyy"
                          disabled={fieldsDisabled}
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { maxWidth: 160, '& .MuiInputBase-input': { padding: '4px 8px' } },
                              inputProps: { 'data-testid': 'create-workboard-task-occurrence-date' },
                            },
                          }}
                        />
                      </ConditionalLoader>
                      <CalendarMonthIcon fontSize="small" color={fieldsDisabled ? 'disabled' : 'primary'} />
                    </DateRow>

                    <StatusRow sx={{ opacity: form.isWaiting ? 0.5 : 1, mt: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <ValuationStatusTracker
                          statusOptions={TASK_STATUS_TRACKER_OPTIONS}
                          currentStatusId={form.status}
                          onStatusChange={statusId => updateForm('status', statusId as WorkboardTask.status)}
                          disabled={fieldsDisabled}
                          fixedHeight="auto"
                          isFromModels
                        />
                      </Box>

                      <FooterBox>
                        {renderUserRoleBox(
                          'assignedTo',
                          'Assigned To',
                          'Select assignees',
                          assigneeInput,
                          setAssigneeInput,
                          'create-workboard-task-assigned-to'
                        )}
                        {renderUserRoleBox(
                          'assignedTaskManager',
                          'Manager',
                          'Select task managers',
                          taskManagerInput,
                          setTaskManagerInput,
                          'create-workboard-task-status-manager'
                        )}
                      </FooterBox>
                    </StatusRow>
                  </Box>
                </StyledBox>

                <Section title="Recurrence">
                  <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="create-workboard-task-recurrence-frequency-label">Frequency</InputLabel>
                      <Select
                        labelId="create-workboard-task-recurrence-frequency-label"
                        label="Frequency"
                        value={form.recurrenceFrequency}
                        onChange={e =>
                          handleRecurrenceFrequencyChange(e.target.value as WorkboardTask.recurrence_frequency)
                        }
                        disabled={isSaving || isDeleting}
                        data-testid="create-workboard-task-recurrence-frequency"
                      >
                        {RECURRENCE_FREQUENCY_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {isRecurring && (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Repeat every"
                        value={form.recurrenceInterval}
                        onChange={e => updateForm('recurrenceInterval', Number(e.target.value) || 1)}
                        disabled={isSaving || isDeleting}
                        inputProps={{ min: 1, 'data-testid': 'create-workboard-task-recurrence-interval' }}
                        helperText={
                          (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.DAILY ? 'days' : '') +
                        (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.WEEKLY ? 'weeks' : '') +
                        (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.MONTHLY ? 'months' : '') +
                        (form.recurrenceFrequency === WorkboardTask.recurrence_frequency.YEARLY ? 'years' : '')
                        }
                      />
                    )}

                    {showSetposPicker && (
                      <FormControl fullWidth size="small">
                        <InputLabel id="create-workboard-task-setpos-label">Monthly pattern</InputLabel>
                        <Select
                          labelId="create-workboard-task-setpos-label"
                          label="Monthly pattern"
                          value={form.recurrenceBySetpos ?? ''}
                          onChange={e => {
                            const value = e.target.value === '' ? null : Number(e.target.value);
                            updateForm('recurrenceBySetpos', value);
                          }}
                          displayEmpty
                          disabled={isSaving || isDeleting}
                          data-testid="create-workboard-task-recurrence-setpos"
                        >
                          <MenuItem value="">
                            <em>Same day of month</em>
                          </MenuItem>
                          {RECURRENCE_SETPOS_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>Optional ordinal weekday pattern (e.g. first Monday).</FormHelperText>
                      </FormControl>
                    )}

                    {showWeekdayPicker && (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                        Weekdays
                        </Typography>
                        <ToggleButtonGroup
                          size="small"
                          exclusive={false}
                          value={form.recurrenceByWeekday}
                          onChange={(_, weekdays) => updateForm('recurrenceByWeekday', weekdays)}
                          disabled={isSaving || isDeleting}
                          aria-label="Recurrence weekdays"
                        >
                          {RECURRENCE_WEEKDAY_OPTIONS.map(option => (
                            <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
                              {option.label}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Box>
                    )}

                    {isRecurring && (
                      <FormControl>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Ends
                        </Typography>
                        <RadioGroup
                          value={form.recurrenceEndMode}
                          onChange={e => updateForm('recurrenceEndMode', e.target.value as RecurrenceEndMode)}
                        >
                          <FormControlLabel
                            value="none"
                            control={<Radio size="small" disabled={isSaving || isDeleting} />}
                            label="Never"
                          />
                          <FormControlLabel
                            value="date"
                            control={<Radio size="small" disabled={isSaving || isDeleting} />}
                            label="On date"
                          />
                          <FormControlLabel
                            value="count"
                            control={<Radio size="small" disabled={isSaving || isDeleting} />}
                            label="After occurrences"
                          />
                        </RadioGroup>
                        {form.recurrenceEndMode === 'date' && (
                          <MobileDatePicker
                            label="Recurrence end date"
                            value={form.recurrenceEndsOn}
                            onChange={date => updateForm('recurrenceEndsOn', date)}
                            minDate={form.occurrenceDate ?? undefined}
                            disabled={isSaving || isDeleting}
                            slotProps={{
                              textField: {
                                size: 'small',
                                sx: { mt: 1, maxWidth: 280 },
                                inputProps: { 'data-testid': 'create-workboard-task-recurrence-ends-on' },
                              },
                            }}
                          />
                        )}
                        {form.recurrenceEndMode === 'count' && (
                          <TextField
                            size="small"
                            type="number"
                            label="Occurrence count"
                            value={form.recurrenceCount ?? ''}
                            onChange={e => updateForm('recurrenceCount', Number(e.target.value) || null)}
                            disabled={isSaving || isDeleting}
                            inputProps={{ min: 1, 'data-testid': 'create-workboard-task-recurrence-count' }}
                            sx={{ mt: 1, maxWidth: 280 }}
                          />
                        )}
                      </FormControl>
                    )}
                  </Stack>
                </Section>
                {workboardTaskOccurrence && (
                  <Section title="Task Notes">
                    <AnalystNotesHistoryPanel
                      referenceType="wo"
                      workboardTaskOccurrence={workboardTaskOccurrence}
                      isDisabled={areNotesDisabled}
                    />
                  </Section>
                )}
              </WorkboardTaskBentoGrid>

              {submitError && (
                <Typography variant="body2" color="error" sx={{ px: 1, pt: 1 }}>
                  {submitError}
                </Typography>
              )}

              <ModalFooter>
                <Button onClick={handleClose} disabled={isSaving || isDeleting}>
                Cancel
                </Button>
                <Button
                  onClick={() => handleSave()}
                  variant="contained"
                  disabled={isSaving || isDeleting}
                  data-testid="create-workboard-task-submit"
                >
                  {isEditMode ? 'Save Task' : 'Create Task'}
                </Button>
              </ModalFooter>
            </>
          )}
        </Box>
      </Modal>
      <Modal
        open={isDeleteConfirmOpen}
        onClose={() => {
          if (!isDeleting) {
            setIsDeleteConfirmOpen(false);
          }
        }}
      >
        <DeleteModalContainer>
          {isDeleting && <LinearProgress color="error" />}
          <DeleteModalCloseButtonContainer>
            <IconButton
              size="small"
              onClick={() => setIsDeleteConfirmOpen(false)}
              disabled={isDeleting}
              aria-label="Close delete task dialog"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DeleteModalCloseButtonContainer>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Delete Task
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={2}>
            <DeleteIcon fontSize="small" color="error" />
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to delete this task? This action cannot be undone.
            </Typography>
          </Box>
          <DeleteModalActions>
            <Button variant="outlined" onClick={() => setIsDeleteConfirmOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={isDeleting}>
              Delete
            </Button>
          </DeleteModalActions>
        </DeleteModalContainer>
      </Modal>
    </>
  );
};

export default CreateWorkboardTaskModal;
